import logger from "@utils/logger";
import type BigNumber from "bignumber.js";
import { bnOrZero } from "kz-ui-sdk";
import { EVENTS } from "./events/constants";
import "./providers";
import { getActiveProvider } from "./registry";

type BalanceAccessor = () => string | BigNumber | number | null | undefined;
let getBalance: BalanceAccessor | null = null;

export function setAnalyticsBalanceAccessor(accessor: BalanceAccessor): void {
  getBalance = accessor;
}

type BalanceSubscribe = (listener: () => void) => () => void;
let subscribeBalance: BalanceSubscribe | null = null;

export function setAnalyticsBalanceSubscriber(subscribe: BalanceSubscribe): void {
  subscribeBalance = subscribe;
}

export function readAnalyticsBalance(): number | undefined {
  return getBalance ? bnOrZero(getBalance()).toNumber() : undefined;
}

// Resolves with the wallet balance once it differs from `from`, or with the current one after `timeoutMs`.
export function waitForBalanceChange(from: number | undefined, timeoutMs: number): Promise<number | undefined> {
  return new Promise((resolve) => {
    if (!subscribeBalance || readAnalyticsBalance() !== from) {
      resolve(readAnalyticsBalance());
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      unsubscribe();
      clearTimeout(timer);
      resolve(readAnalyticsBalance());
    };
    const unsubscribe = subscribeBalance(() => {
      if (readAnalyticsBalance() !== from) finish();
    });
    const timer = setTimeout(finish, timeoutMs);
  });
}

type MemberIdAccessor = () => string | null | undefined;
let getMemberId: MemberIdAccessor | null = null;

export function setAnalyticsMemberIdAccessor(accessor: MemberIdAccessor): void {
  getMemberId = accessor;
}

const BALANCE_ENRICHED_EVENTS: ReadonlySet<string> = new Set([
  EVENTS.CLICK_DEPOSIT,
  EVENTS.TOP_UP_SUBMIT,
  EVENTS.DEPOSIT_CREATE_RESULT,
  EVENTS.PROMO_CLAIM_ATTEMPT,
  EVENTS.CLICK_WITHDRAWAL,
  EVENTS.WITHDRAWAL_SUBMIT,
]);

/** Canonical promotion journey events — spec requires `memberId` inline on each. */
const MEMBER_ID_ENRICHED_EVENTS: ReadonlySet<string> = new Set([
  EVENTS.PROMOTION_PRESENTED,
  EVENTS.PROMOTION_ACTION,
  EVENTS.PROMOTION_CLAIM_RESULT,
  EVENTS.PROMOTION_ISSUED,
]);

export function dispatchAnalyticsEvent(eventName: string, metadata?: Record<string, any>, value?: number): void {
  const provider = getActiveProvider();
  let enrichedMetadata = metadata;

  if (getBalance && BALANCE_ENRICHED_EVENTS.has(eventName)) {
    enrichedMetadata = { ...enrichedMetadata, accountBalance: bnOrZero(getBalance()).toNumber() };
  }

  if (getMemberId && MEMBER_ID_ENRICHED_EVENTS.has(eventName)) {
    enrichedMetadata = { ...enrichedMetadata, memberId: getMemberId() ?? null };
  }

  logger._console.log(`[analytics:${provider?.name ?? "none"}] ${eventName}`, { value, metadata: enrichedMetadata });
  provider?.logEvent(eventName, enrichedMetadata, value);
}
